/* eslint-disable @typescript-eslint/no-var-requires */
import { isDevelopment } from '@/utils';
import { isDebug } from '@/modules/config';
import { isBrowser } from './detector';
import { isBoolean, isFunction, isInvalid, isNumber, isObject, isString } from './validate';
import { getBrowser } from './utils';
/**
 * 日志输出
 */
export const loggerType: { [key: string]: any } = {
  LOG: 'log', // 信息：输出应用运行过程的详细信息。
  WARN: 'warn', // 警告：输出可能潜在的危险状况。
  DEBUG: 'debug', // 调试：输出更细致的对调试应用有用的信息。
  ERROR: 'error', // 错误：输出错误，但应用还能继续运行。
  INFO: 'info', // 信息：输出应用运行过程的详细信息。
  TRACE: 'trace', // 跟踪：输出更细致的程序运行轨迹。
  // ALL: 'ALL', // 所有：输出所有级别信息。
  // FATAL: 'FATAL', // 致命：输出非常严重的可能会导致应用程序终止的错误。
  // OFF: 'OFF', // 关闭：最高级别，不输出日志。
};

let loggerBase = '@hst/utils';
export const loggerSetBase = (base: string) => {
  loggerBase = base;
};

/**
 * 通用日志输出
 * @param text 日志信息
 * @param type 输出类型
 */
export const loggerLog = (
  { message, type }: { message: unknown; type: string },
  module?: string
): void => {
  if (!isDebug) return;
  if (typeof message === 'string') {
    console[type](`${loggerBase}:${module ? `${module}:` : ''}${message}`);
    return;
  }
  try {
    console[type](`${loggerBase}:${module ? `${module}:` : ''}`, ...(message as any));
  } catch {
    console[type](`${loggerBase}:${module ? `${module}:` : ''} ${message}`);
  }
};
/**
 * 信息日志输出
 * @param text 日志信息
 */
export const loggerInfo = (message: unknown, module?: string): void => {
  loggerLog(
    {
      type: loggerType.INFO,
      message,
    },
    module
  );
};

/**
 * 警告日志输出
 * @param text 日志信息
 */
export const loggerWarn = (message: unknown, module?: string): void => {
  loggerLog(
    {
      type: loggerType.WARN,
      message,
    },
    module
  );
};

/**
 * 错误日志输出
 * @param text 日志信息
 */
export const loggerError = (message: unknown, module?: string): void => {
  loggerLog(
    {
      type: loggerType.ERROR,
      message,
    },
    module
  );
};

/**
 * 获取服务器名称
 */
export const getHostname = (): string => {
  let hostname = '';
  if (isBrowser()) hostname = getBrowser().location.hostname || 'Window';
  else {
    const os = require('os');
    if (isDevelopment()) hostname = os?.hostname();
    else hostname = os?.hostname()?.split('-').slice(-2).join('-');
  }
  return hostname;
};

/**
 * 获取唯一请求ID
 */
export const getRequestID = () => {
  const { v4 } = require('uuid');
  return v4().replace(/-/g, '');
};

/**
 * 创建日志函数
 */
const createLogger = (opts = {}) => {
  const { ev, pkgName } = { ev: loggerType.LOG, pkgName: '', ...opts };
  return function (...args: any[]) {
    let reqId = getRequestID();
    let hostname = getHostname();
    let logType = `[BIZ]`;
    const msgs: any[] = [];
    // 赋值积累日志内容
    const assignment = ({
      arg,
    }: {
      arg:
        | { reqId: string; hostname: string; logType: string; msg: any; [key: string]: any }
        | string;
    }) => {
      msgs.push(arg);
      if (isObject(arg, true)) {
        const {
          reqId: argReqID,
          hostname: argHostname,
          logType: argLogType,
          msg: argMsg,
          ...argOther
        } = arg;
        reqId = argReqID || reqId;
        hostname = argHostname || hostname;
        logType = argLogType || logType;
        msgs[msgs.length - 1] = argMsg || argOther;
      }
    };
    args.forEach(
      (
        arg:
          | { reqId: string; hostname: string; logType: string; msg: any; [key: string]: any }
          | string,
        index: number
      ) => {
        const isPrint = index >= args.length - 1;
        // 赋值积累日志内容
        assignment({ arg });
        // 将积累的内容进行打印
        if (isPrint) {
          const levelItem = `[${ev.toUpperCase()}]`;
          const hostNameItem = `[${hostname}]`;
          const reqIdItem = `[${reqId}]`;
          const pkgNameItem = `[${pkgName}]`;
          const traceItem = `logger`;
          const message = `${levelItem} ${logType} ${hostNameItem} ${reqIdItem} ${pkgNameItem} ${traceItem} - `;
          const event = console.origin[loggerType.LOG];
          if (isFunction(event)) {
            if (msgs.length > 1) {
              try {
                event(message, ...msgs);
              } catch (error) {
                event(message, msgs);
              }
            } else {
              const msg = msgs?.[0];
              if (isString(msg) || isNumber(msg) || isInvalid(msg) || isBoolean(msg))
                event(message + msg);
              else event(message, msg);
            }
          }
        }
      }
    );
  };
};

/**
 * 收集源函数
 */
const collectOrigin = (opts = {}) => {
  for (const key in loggerType) {
    const ev = loggerType[key];
    if (!isObject(console.origin, true)) console.origin = {};
    console.origin[ev] = console[ev];
    console[ev] = createLogger({ ev, ...opts });
  }
};

/**
 * 日期: [日志级别(大写)][日志类别(大写)][线程名字][请求ID][应用名称] 调用栈 - message
 */
export const loggerNodeRewrite = (opts = {}) => {
  const { pkgName } = { pkgName: '', ...opts };
  if (console?.isRewrite) return; // 已经重写过
  if (isBrowser()) return; // 浏览器环境不重写
  console.isRewrite = true;
  collectOrigin({ pkgName });
};

/**
 * http 日志
 */
export const httpLogger = (opts: { config?: any; extra?: any } = {}) => {
  const { config, extra, func } = { func: loggerType.LOG, ...opts };
  if (isInvalid(config)) return;
  let msg = `[Http URL]: ${
    config.url
  } [Method]: ${config.method.toLocaleUpperCase()} [Params]: ${JSON.stringify(
    config.params || {}
  )} [Data]: ${isObject(config.data) ? JSON.stringify(config.data) : config.data}`;
  if (isString(extra)) msg += ` ${extra}`;
  console[func]({ reqId: config.reqId, logType: '[API]', msg });
};
